import { ShieldAlert, ShieldCheck, Wrench } from "lucide-react";
import type { TurnVerifyResult } from "../../domain/types";
import { cn } from "../common/cn";

interface VerifyBadgeProps {
  verify: TurnVerifyResult;
  healJumps: number;
}

/**
 * Kalite Planı — `turn.verify` sonucunu transcript'te görünür kılar.
 * CitationAuditBadge ile aynı görsel dil (rounded-full pill + title
 * tooltip). Üç durum:
 *
 *   • ok && !material && healJumps=0 → "Doğrulandı" (yeşil) — tur
 *     denetimden temiz çıktı.
 *   • material || healJumps>0        → "Düzeltildi (N)" (turuncu) —
 *     engine self-heal döngüsü çalıştı; tooltip issue kind'larını döker.
 *   • issues var ama material değil  → sarı uyarı — bilgilendirici
 *     bulgular, metin değiştirilmedi.
 *
 * Event hiç gelmezse bileşen render edilmez (TranscriptTurn'daki
 * `verify ? <VerifyBadge/> : null` koşulu) — eski davranış korunur.
 */
export function VerifyBadge({ verify, healJumps }: VerifyBadgeProps) {
  const issues = verify.issues ?? [];
  const healed = verify.material || healJumps > 0;
  const hasIssues = issues.length > 0;

  const issueKinds = Array.from(
    new Set(issues.map((i) => i.kind).filter((k) => k.length > 0)),
  );

  const titleParts: string[] = [];
  if (healed) {
    titleParts.push(
      `Doğrulayıcı materyal sorun buldu; metin ${Math.max(healJumps, 1)} onarım turundan geçti.`,
    );
  } else if (hasIssues) {
    titleParts.push(
      `Doğrulayıcı ${issues.length} bilgilendirici bulgu işaretledi; metin değiştirilmedi.`,
    );
  } else if (verify.ok) {
    titleParts.push("Doğrulayıcı denetiminden temiz geçti.");
  } else {
    titleParts.push("Doğrulama tamamlanamadı — sonuç belirsiz.");
  }
  if (issueKinds.length > 0) {
    titleParts.push(`Bulgu türleri: ${issueKinds.join(", ")}`);
  }
  const highCount = issues.filter((i) => i.severity === "high").length;
  if (highCount > 0) {
    titleParts.push(`${highCount} yüksek önem dereceli bulgu.`);
  }
  if (verify.judgeUsed) {
    titleParts.push("LLM hakem (judge) devreye girdi.");
  }

  const label = healed
    ? `Düzeltildi (${Math.max(healJumps, 1)})`
    : hasIssues
      ? `${issues.length} bulgu`
      : verify.ok
        ? "Doğrulandı"
        : "Doğrulama uyarısı";

  const Icon = healed ? Wrench : hasIssues || !verify.ok ? ShieldAlert : ShieldCheck;

  return (
    <span
      role="status"
      title={titleParts.join(" | ")}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider",
        healed
          ? "border-orange-400/55 bg-orange-500/12 text-orange-200"
          : hasIssues || !verify.ok
            ? "border-amber-400/55 bg-amber-500/12 text-amber-200"
            : "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      <span className="normal-case font-normal">{label}</span>
    </span>
  );
}
